import React, {
  forwardRef,
  useState,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Flex,
  Typography,
  message,
  Select,
  DatePicker,
  Spin,
} from "antd";
import { EditOutlined, BarsOutlined,DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import UploadFile from "@/components/UplaodFile";
const DetailModal = forwardRef((props, ref) => {
  const { onSuccess } = props;
  const childRef = useRef();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const [form] = Form.useForm();
  const [files, setFiles] = useState([]);
  const [statementFiles, setStatementFiles] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [pdfData, setPdfData] = useState([]);
  const [pdfUrl, setPdfUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  // ===== 暴露方法 =====
  useImperativeHandle(ref, () => ({
    open: (data) => {
      setRecord(data);
      setSelectedDate(data.date);
      setOpen(true);
      // console.log("选中的日期：", data.date.format("YYYY-MM-DD"));
    },
  }));
  const safePunchTime = (arr) => {
    if (!arr || arr.length < 5) return null;
    const [y, m, d, h, min, s = 0] = arr;
    return dayjs(`${y}-${m}-${d} ${h}:${min}:${s}`, "YYYY-M-D H:m:s");
  };

  useEffect(() => {
    if (!open || !record) return;

    const initForm = (data) => {
      form.setFieldsValue({
        punchType: data.punchType ?? null,
        punchTime: safePunchTime(data.punchTime),
        remark: data.remark ?? "",
      });
     
      if (data.fileList?.length) {
        const uploadFileList = mapApiFilesToUpload(data.fileList);
        childRef.current?.setFileList(uploadFileList);
        const { pdfs } = splitFiles(uploadFileList);
        setPdfData(
          pdfs.map((item) => ({
            fileName: item.name,
            url: item.url,
            fileId:item.uid
          })),
        );
        if (pdfs.length) {
          setPdfUrl(pdfs[0].url);
        }
      }
    };

    initForm(record);

    if (
      record.attendanceId &&
      (!record.punchTime || record.punchTime.length < 5)
    ) {
      const fetchDetail = async () => {
        setLoading(true);
        try {
          const res = await http.get(api.getDetailPayRollById, {
            params: { officeTimeSheetId: record.attendanceId },
          });
          if (res?.code === "1") {
            initForm(res.data);
          }
        } finally {
          setLoading(false);
        }
      };
      fetchDetail();
    }
  }, [open, record]);
  const splitFiles = (files = []) => {
    const images = [];
    const pdfs = [];

    files.forEach((item) => {
      if (item.suffix?.toLowerCase() === "pdf") {
        pdfs.push(item);
      } else {
        images.push(item);
      }
    });

    return { images, pdfs };
  };
  const mapApiFilesToUpload = (apiFiles = []) =>
    apiFiles.map((item) => ({
      uid: String(item.fileId),
      name: item.url.split("/").pop(),
      status: "done",
      url: item.url,
      suffix: item.suffix,
      thumbUrl: item.suffix !== "pdf" ? item.url : undefined,
    }));

  // ===== 编辑提交 =====
  const onFinish = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const params = {
        attendanceId: record.attendanceId,
        // punchTime: values.punchTime.format("YYYY-MM-DD HH:mm:ss"),
        remark: values.remark || "",
      };
      const res = await http.post(api.getEditAttendance, params);
      if (res?.code !== "1") {
        message.error(res?.msg || "Update failed");
        return;
      }
      message.success("Attendance Updated Successfully");
      setOpen(false);
      form.resetFields();
      setFiles([]);
      setStatementFiles([]);
      onSuccess?.();
    } finally {
      setLoading(false);
    }
  };

  const addUpload = async (data) => {
    // 检查类型
    const invalidFiles = data.filter((item) => {
      const type = item.originFileObj?.type;
      if (!type) return false;
      return (
        type !== "image/jpeg" &&
        type !== "image/png" &&
        type !== "application/pdf"
      );
    });

    if (invalidFiles.length > 0) {
      const fileNames = invalidFiles.map((item) => `"${item.name}"`).join(", ");
      message.error(`The following files are not valid: ${fileNames}`);
      childRef.current?.clearFileList();
      return;
    }
    setUploading(true);
    try {
      const uploadedFiles = await uploadFiles(
        data.filter((item) => item.originFileObj),
      ); 
      setFiles((prev) => [...prev, ...uploadedFiles]);

      if (uploadedFiles.length > 0 && record?.attendanceId) {
        const fileParams = {
          managerAttendanceId: record.attendanceId,
          fileSet: uploadedFiles,
        };
        const fileRes = await http.post(api.getAddAttendanceFiles, fileParams);
        if (fileRes?.code !== "1") {
          message.error(fileRes?.msg || "File upload failed");
          return;
        }
        message.success("Files uploaded successfully");
      }
    } catch (error) {
      message.error("File upload failed.");
    } finally {
      setUploading(false);
    }
  };

  async function uploadFiles(data) {
    const uploaded = [];
    for (const item of data) {
      const formData = new FormData();
      formData.append("files", item.originFileObj);

      const response = await http.post(api.getUpdateFileDict, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.code === "1") {
        uploaded.push(response.data[0]);
      } else {
        throw new Error(response.msg || "Upload failed");
      }
    }
    return uploaded;
  }

  // 删除文件
  const handleDeleteFile = async (file) => {
  if (!record?.attendanceId) return;
   console.log('file',file);
  Modal.confirm({
    title: "Confirm",
    content: `Are you sure to delete "${file.fileName}"?`,
    okText: "Yes",
    cancelText: "No",
    onOk: async () => {
      try {
        await http.get(api.getDeleAttendanceFiles, {
          params: {
            managerAttendanceFileId: file.fileId,
          },
        });
        message.success("File deleted successfully");
        setPdfData((prev) => prev.filter((f) => f.url !== file.url));
        if (pdfUrl === file.url) setPdfUrl("");
      } catch (err) {
        console.error(err);
        message.error("Failed to delete file");
      }
    },
  });
};
// 关闭弹窗

const handleClose = () => {
  setOpen(false);
  form.resetFields();     
  setFiles([]);            
  setStatementFiles([]);    
  setPdfData([]);         
  setPdfUrl("");         
  childRef.current?.clearFileList(); 
};

  return (
    <Modal
      open={open}
      width="70%"
      maskClosable={false}
      destroyOnClose
      title={
        <Flex align="center">
          <EditOutlined style={{ marginRight: 8 }} />
          <Typography.Title level={4} style={{ margin: 0 }}>
            Attendance Detail
          </Typography.Title>
        </Flex>
      }
      onCancel={() => {
        setOpen(false);
        form.resetFields();
      }}
      footer={[
       <Button key="cancel" onClick={handleClose}>
      Cancel
    </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading || uploading}
          onClick={() => form.submit()}
        >
          Submit
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div className="model_top" style={{ width: "100%" }}></div>
        <div className="model_content">
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ style: { width: 200 } }}
            wrapperCol={{ style: { flex: 1 } }}
            onFinish={onFinish}
          >
            <Flex align="flex-start" justify="space-between" gap={32}>
              <div style={{ flex: 1 }}>
                <Form.Item label="Punch Type" name="punchType">
                  <Select
                    disabled
                    options={[
                      { value: 1, label: "AM Clock In" },
                      { value: 2, label: "AM Clock Out" },
                      { value: 3, label: "PM Clock In" },
                      { value: 4, label: "PM Clock Out" },
                    ]}
                  />
                </Form.Item>

                {/* <Form.Item
                  label="Punch Time"
                  name="punchTime"
                  rules={[{ required: true }]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="YYYY-MM-DD hh:mm A"
                    use12Hours
                    showTime={{
                      format: "hh:mm A",
                      use12Hours: true,
                    }}
                    disabledDate={(current) => {
                      if (!selectedDate) return true;
                      return !current.isSame(selectedDate, "day");
                    }}
                    disabledTime={(date) => {
                      if (!date || !selectedDate) return {};

                      const now = dayjs();

                      if (!date.isSame(now, "day")) return {};

                      const currentHour = now.hour();
                      const currentMinute = now.minute();

                      return {
                        disabledHours: () =>
                          Array.from({ length: 24 }, (_, h) => h).filter(
                            (h) => h > currentHour,
                          ),

                        disabledMinutes: (selectedHour) =>
                          selectedHour === currentHour
                            ? Array.from({ length: 60 }, (_, m) => m).filter(
                                (m) => m > currentMinute,
                              )
                            : [],
                      };
                    }}
                  />
                </Form.Item> */}

                <Form.Item label="Remark" name="remark">
                  <Input.TextArea maxLength={200} showCount />
                </Form.Item>
              </div>
              <div style={{ flex: 1 }}>
                {/* upload */}
                <div
                  style={{
                    marginBottom: "16px",
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  Upload PDF image
                </div>

                <div className="uploadText">
                  Uploaded files only support JPG, PNG or PDF.
                </div>

                {/* Upload 区域 */}
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontWeight: "bold", marginBottom: 8 }}>
                    Upload
                  </div>

                  <Form.Item name="fileSet">
                    <UploadFile ref={childRef} sendDataToParent={addUpload} />
                  </Form.Item>
                </div>

                {pdfData.length > 0 && (
                  <div
                    style={{
                      marginTop: 24,
                      display: "flex",
                      gap: 16,
                      height: "50vh",
                    }}
                  >
                    <div
                      className="mainName"
                      style={{
                        width: 200,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        borderRight: "1px solid #eee",
                        paddingRight: 12,
                        overflowY: "auto",
                      }}
                    >
                    {pdfData.map((item) => (
  <div
    key={item.fileId}
    style={{
      cursor: "pointer",
      padding: "6px 8px",
      borderRadius: 4,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      color: pdfUrl === item.url ? "#1890ff" : "#000",
      fontWeight: pdfUrl === item.url ? 500 : 400,
    }}
  >
    <div
      style={{ display: "flex", alignItems: "center" }}
      onClick={() => setPdfUrl(item.url)}
    >
      <BarsOutlined style={{ marginRight: 8 }} />
      {item.fileName}
    </div>

    {record?.attendanceId && (
      <DeleteOutlined
        style={{ color: "red", cursor: "pointer" }}
        onClick={(e) => {
          e.stopPropagation(); 
          handleDeleteFile(item);
        }}
      />
    )}
  </div>
))}
                    </div>

                    <div
                      className="PDFbox"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: "100%",
                        border: "1px solid #eee",
                        borderRadius: 4,
                      }}
                    >
                      {pdfUrl ? (
                        <iframe
                          src={pdfUrl}
                          title="PDF Viewer"
                          width="100%"
                          height="100%"
                          style={{
                            border: "none",
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#999",
                          }}
                        >
                          Select a PDF to preview
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Flex>
          </Form>
        </div>
      </Spin>
    </Modal>
  );
});

export default DetailModal;
